/**
 * @fileoverview OpsAnalyticsRollupPanel — Operations → Analytics rollup subtab.
 *
 * Renders the state of the daily `analytics_daily` rollup (latest day, last-30d
 * populated-day count, row totals, last-7d coverage) and lets an operator
 * trigger `POST /ops/analytics-rollup/run` for a chosen window. Wraps the two
 * backend endpoints in api.opsAnalyticsRollupStatus / opsAnalyticsRollupRun.
 *
 * Cadence: manual only. The scheduled cron `analytics-rollup.yml` (04:40 UTC)
 * covers the routine case; this panel exists to (a) show operators the state
 * without opening the DB and (b) let them recompute after a schema change or
 * a missed cron run without hunting for the workflow_dispatch button on GitHub.
 */

import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';

const DAYS_BACK_OPTIONS = [3, 7, 30, 90];

/** Format an ISO timestamp for the status tile as `YYYY-MM-DD HH:MM UTC`. */
function fmtIsoUtc(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return `${d.toISOString().slice(0, 10)} ${d.toISOString().slice(11, 16)} UTC`;
  } catch (_) {
    return iso;
  }
}

/**
 * Analytics rollup subsystem panel.
 *
 * @param {Object} props
 * @param {string} props.adminApiPrefix - Same prefix that CRM ops uses; passed
 *   down from SuperAdmin.js so the tabpanel stays consistent with the rest of
 *   the Operations group.
 * @returns {JSX.Element}
 */
function OpsAnalyticsRollupPanel({ adminApiPrefix }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [daysBack, setDaysBack] = useState(3);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);

  const fetchStatus = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.opsAnalyticsRollupStatus(adminApiPrefix);
      setStatus(data);
    } catch (err) {
      setError('Could not load analytics rollup status.');
    } finally {
      setLoading(false);
    }
  }, [adminApiPrefix]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleRun = async () => {
    setRunning(true);
    setRunResult(null);
    try {
      const { data } = await api.opsAnalyticsRollupRun(adminApiPrefix, daysBack);
      setRunResult({ ok: true, days_touched: data.days_touched || [] });
      await fetchStatus();
    } catch (err) {
      setRunResult({ ok: false, error: err?.response?.data?.error || 'Run failed' });
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="ops-panel" data-testid="ops-analytics-rollup-panel">
        <p role="status">Loading analytics rollup status…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ops-panel" data-testid="ops-analytics-rollup-panel">
        <div className="empty-state" role="alert">
          <p>{error}</p>
          <button type="button" className="btn btn-secondary" onClick={fetchStatus}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const totals = status?.row_totals || {};

  return (
    <div className="ops-panel" data-testid="ops-analytics-rollup-panel">
      <h2>Analytics rollup</h2>
      <p className="ops-panel__blurb">
        Recomputes the last N days of <code>analytics_daily</code> from raw
        sessions + events. Idempotent — safe to re-run. The scheduled cron
        (<code>analytics-rollup.yml</code>) runs at 04:40 UTC daily.
      </p>

      <div className="card ops-panel__status" data-testid="ops-analytics-status">
        <dl className="ops-panel__dl">
          <div>
            <dt>Latest day rolled up</dt>
            <dd data-testid="ops-analytics-latest-day">
              {status?.latest_day ? status.latest_day : '—'}
            </dd>
          </div>
          <div>
            <dt>Last updated</dt>
            <dd>{fmtIsoUtc(status?.latest_updated_at)}</dd>
          </div>
          <div>
            <dt>Days populated (last 30)</dt>
            <dd data-testid="ops-analytics-days-populated">
              {status?.days_populated_last_30 ?? 0} / 30
            </dd>
          </div>
          <div>
            <dt>Last 30d totals</dt>
            <dd className="ops-panel__mono">
              {(totals.pageviews || 0).toLocaleString()} pageviews ·{' '}
              {(totals.sessions || 0).toLocaleString()} sessions ·{' '}
              {(totals.users || 0).toLocaleString()} users
            </dd>
          </div>
        </dl>
      </div>

      <div className="ops-panel__actions" data-testid="ops-analytics-actions">
        <label className="ops-panel__label" htmlFor="ops-analytics-days">
          Days to recompute
          <select
            id="ops-analytics-days"
            value={daysBack}
            onChange={(e) => setDaysBack(parseInt(e.target.value, 10))}
            data-testid="ops-analytics-days-select"
            disabled={running}
          >
            {DAYS_BACK_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleRun}
          disabled={running}
          data-testid="ops-analytics-run-btn"
        >
          {running ? 'Running…' : 'Run rollup now'}
        </button>
      </div>

      {runResult && (
        <div
          className={`ops-panel__result ops-panel__result--${runResult.ok ? 'ok' : 'err'}`}
          role="status"
          data-testid="ops-analytics-result"
        >
          {runResult.ok
            ? `Rolled up ${runResult.days_touched.length} day(s): ${runResult.days_touched.join(', ')}`
            : `Failed: ${runResult.error}`}
        </div>
      )}

      {status?.coverage_last_7?.length ? (
        <div className="tbl-wrap" data-testid="ops-analytics-coverage">
          <table>
            <thead>
              <tr>
                <th scope="col">Day</th>
                <th scope="col">Pageviews</th>
                <th scope="col">Sessions</th>
                <th scope="col">Users</th>
                <th scope="col">Events</th>
              </tr>
            </thead>
            <tbody>
              {status.coverage_last_7.map((r) => (
                <tr key={r.day}>
                  <td>{r.day}</td>
                  <td className="td-amt">{r.pageviews}</td>
                  <td className="td-amt">{r.sessions}</td>
                  <td className="td-amt">{r.users}</td>
                  <td className="td-amt">{r.events}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="empty-state" data-testid="ops-analytics-empty">
          No <code>analytics_daily</code> rows in the last 7 days. Click{' '}
          <strong>Run rollup now</strong> to populate.
        </p>
      )}
    </div>
  );
}

export default OpsAnalyticsRollupPanel;
